import { useState } from 'react';
import { ACCOUNT_DELETE_CONFIRMATION_PHRASE, typedPhraseMatches, CHURN_REASONS, CHURN_REASON_LABELS, churnCommentPlaceholder, type ChurnReason } from '@overlayqa/shared';
import { useAuth, useClerk } from '@/lib/auth';
import { accountApi } from '@/lib/api/account';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';

/**
 * Self-serve account deletion.
 *
 * Clerk's built-in delete action (avatar menu > Manage account > Security)
 * requires reverification and accepts only password / email code / phone code.
 * 153 of 175 production users (87%, measured 2026-08-05) signed up with Google
 * OAuth and have none of those factors, so Clerk answers "no suitable
 * authentication factor" and they cannot delete their account at all. This
 * section calls our own server route, which deletes through the Clerk Backend
 * API and needs no client reverification.
 */

/** Matches the phrase the server requires, and the wording Clerk's own modal asks for. */
const CONFIRMATION_PHRASE = ACCOUNT_DELETE_CONFIRMATION_PHRASE;

export function DeleteAccountSection() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<ChurnReason | ''>('');
  const [comment, setComment] = useState('');

  function close() {
    if (deleting) return;
    setOpen(false);
    setError(null);
    setReason('');
    setComment('');
  }

  /**
   * `typed` is the phrase from the dialog's own input. Re-checked HERE as well
   * as by the button's `disabled`, because this is the single most destructive
   * action in the product and a lone disabled attribute is one stray submit
   * away from being the only thing standing there. The two extension project
   * confirms keep the same second gate (ProjectSettings handleConfirmDelete's
   * `if (!canDelete) return;`). Same predicate as the button, so a phrase the
   * user can type past the button is never refused here.
   */
  async function handleDelete(typed: string) {
    if (deleting || !reason || !typedPhraseMatches(typed, CONFIRMATION_PHRASE)) return;
    setDeleting(true);
    setError(null);
    try {
      const token = await getToken();
      await accountApi.deleteAccount(token ?? undefined, CONFIRMATION_PHRASE, {
        reason,
        comment: comment.trim() || undefined,
      });
      // The Clerk user is gone, so the session is already invalid; signing out
      // clears it locally and lands them somewhere that renders.
      await signOut({ redirectUrl: '/sign-in' });
    } catch (err: unknown) {
      // Never leave a failed deletion silent: the user is trying to leave and
      // has to know whether they succeeded.
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't delete your account. Please try again."
      );
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-700">
            Permanently delete your account and everything in it: projects, issues, comments, and
            integrations.
          </p>
          <p className="mt-1 text-sm text-gray-500">This cannot be undone.</p>
        </div>
        <button
          type="button"
          className="btn-secondary text-red-600 whitespace-nowrap"
          onClick={() => setOpen(true)}
        >
          <span className="text-cap-trim">Delete account</span>
        </button>
      </div>

      <DeletionConfirmDialog
        open={open}
        request={{ kind: 'account', ids: [] }}
        fallback={{
          title: 'Delete your account?',
          message: 'This permanently deletes your account and all of your projects, issues, and comments. It cannot be undone.',
          confirmLabel: 'Delete my account',
          typedPhrase: CONFIRMATION_PHRASE,
        }}
        loading={deleting}
        error={error}
        onConfirm={handleDelete}
        onCancel={close}
        confirmDisabled={!reason}
        maxWidthClass="max-w-xl"
      >
        <fieldset className="mb-5" disabled={deleting}>
          <legend className="text-sm font-medium text-gray-900 mb-2">What made you decide to delete your account?</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CHURN_REASONS.map((value) => (
              <label key={value} className={`flex items-center gap-2 rounded-lg border p-3 text-sm cursor-pointer ${reason === value ? 'border-primary-500 bg-primary-50 text-gray-900' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                <input type="radio" name="account-deletion-reason" value={value}
                  checked={reason === value} onChange={() => setReason(value)}
                  className="accent-primary-600" />
                <span>{CHURN_REASON_LABELS[value]}</span>
              </label>
            ))}
          </div>
          <label htmlFor="account-deletion-comment" className="field-label mt-4">Anything else? <span className="font-normal text-gray-500">(optional)</span></label>
          <textarea id="account-deletion-comment" className="input w-full min-h-20 resize-y"
            value={comment} onChange={(event) => setComment(event.target.value)} maxLength={2000}
            placeholder={churnCommentPlaceholder(reason)} />
          <p className="mt-2 text-xs text-gray-500">We’ll keep this feedback after your account is deleted.</p>
        </fieldset>
      </DeletionConfirmDialog>
    </>
  );
}
